import type { FormatterCallbackFunction, Point } from "highcharts"
import { SERIE_FORMAT } from "./ChartOptions"
import type { Formatter, FormatterOptions } from "./sharedFormatter"
import { getValueFormatter } from "./tooltipValueFormatters"

/**
 * A row's swatch and label.
 *
 * The label carries no weight of its own so it inherits the row's — normal for
 * a data row, and the heavier weight the total row sets on its wrapper. It
 * used to pin `font-normal`, which left the total's label light while its
 * value went bold.
 */
function wrapWithSeriesTitleHtml(serie: string, color?: Point["color"]): string {
  return `
    <div class="flex items-center gap-1.5 w-max-content flex-1">
    ${color ? `<span class="w-2.5 h-2.5 rounded-sm" style="background: ${escapeHtml(String(color))}"></span>` : ""}
      <span class="truncate flex-1 max-w-[35ch]">${escapeHtml(serie)}</span>
    </div>`
}

// Series names come from user-typed Studio labels and the tooltip is rendered
// as HTML, so they must be escaped or a label can inject markup into it.
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/** @deprecated */
export function wrapWithValuesHtml(values: string[]): string {
  return `<div class="text-right whitespace-nowrap" style="font-family: var(--font-geist-mono)">${values.join(" ")}</div>`
}

const pricingFormatter: Formatter = function pricingFormatter() {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.PRICE)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(this.series.name, this.color)}${wrapWithValuesHtml([
      `<span class="tracking-[1px]"></span> ${formattedValue}`,
    ])}`
  }
}

const ratioFormatter: Formatter = function ratioFormatter() {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.RATIO)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const numberFormatter: Formatter = function numberFormatter(options) {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.NUMBER, options)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const tokenFormatter: Formatter = function tokenFormatter(options) {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.TOKEN, options)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const percentageFormatter: Formatter = function percentageFormatter(options) {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.PERCENTAGE, options)
  return function () {
    const formattedValue = valueFormatter.call(this, this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const timeFormatter: Formatter = function timeFormatter() {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.TIME)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const bytesFormatter: Formatter = function bytesFormatter() {
  const valueFormatter = getValueFormatter(SERIE_FORMAT.BYTES)
  return function () {
    const formattedValue = valueFormatter(this.y || 0)
    return `${wrapWithSeriesTitleHtml(
      this.series.name,
      this.color,
    )}${wrapWithValuesHtml([formattedValue])}`
  }
}

const formatters = {
  [SERIE_FORMAT.PRICE]: pricingFormatter,
  [SERIE_FORMAT.RATIO]: ratioFormatter,
  [SERIE_FORMAT.PERCENTAGE]: percentageFormatter,
  [SERIE_FORMAT.NUMBER]: numberFormatter,
  [SERIE_FORMAT.TOKEN]: tokenFormatter,
  [SERIE_FORMAT.TIME]: timeFormatter,
  [SERIE_FORMAT.BYTES]: bytesFormatter,
  [SERIE_FORMAT.TOKEN_ICP]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ICP",
    }),
  [SERIE_FORMAT.TOKEN_CKBTC]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ckBTC",
    }),
  [SERIE_FORMAT.TOKEN_CKETH]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ckETH",
    }),
  [SERIE_FORMAT.CYCLES_PER_SECONDS]: (options: FormatterOptions) =>
    numberFormatter({
      ...options,
      postfix: "cycles/second",
    }),
  [SERIE_FORMAT.KWH]: (options: FormatterOptions) =>
    numberFormatter({
      ...options,
      postfix: "kWh",
    }),
} as const

/** @deprecated */
export function getFormatter(
  serie: `${SERIE_FORMAT}`,
  options: FormatterOptions,
): FormatterCallbackFunction<Point> {
  return formatters[serie](options)
}
